import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Checkbox } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import MultilineGridHeader from "@entrilia/shared/components/grid/MultilineGridHeader";
import { numberComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import type { Category } from "../../../../api/types/accessTypes";
import createCheckboxColumn from "../../../common/grid/createCheckboxColumn";
import { GroupedRowCell } from "../../../common/grid/GroupedRowCell";
import PortalRoleChip from "../../../common/PortalRoleChip";
import PortalRoleSelector from "../../../common/PortalRoleSelector";
import type { CommunicationMatrixModel } from "./matrixHelper";
import { orderedPortalRoles, portalRowToArray, resolvePortalRoles } from "./matrixHelper";

interface CommunicationMatrixColumnsParams {
  messageCategories: Category[];
  excludedColumns?: string[];
  onChange: (model: CommunicationMatrixModel) => void;
  onRemove: (ids: string[]) => void;
  readOnly: boolean;
  rowEditingDisabled: (model: CommunicationMatrixModel) => boolean;
  childRowActionsDisabled?: boolean;
  groupRowActionsDisabled?: boolean;
}

export const getCommunicationMatrixColumns = ({
  messageCategories,
  excludedColumns,
  onChange,
  onRemove,
  readOnly,
  rowEditingDisabled,
  childRowActionsDisabled,
  groupRowActionsDisabled,
}: CommunicationMatrixColumnsParams): GridColDef<CommunicationMatrixModel>[] => {
  const onCategoryChange = (model: CommunicationMatrixModel, category: Category, checked: boolean) => {
    const categories = model.categories.filter((categoryId) => categoryId !== category.externalId);
    if (checked) {
      categories.push(category.externalId);
    }
    onChange({ ...model, categories });
  };

  const categoriesColumns: GridColDef<CommunicationMatrixModel>[] = [
    {
      sortable: false,
      resizable: false,
      field: "divider",
      width: 1,
      renderCell: ({ rowNode }) =>
        GroupedRowCell(rowNode, <Box sx={{ borderRight: 1, borderColor: "divider", height: "100%" }} />),
      headerName: "",
    },
  ];

  messageCategories.sort(numberComparerBy((c) => c.sortOrder)).forEach((category) => {
    categoriesColumns.push({
      sortable: false,
      field: category.id,
      flex: 1,
      minWidth: 100,
      maxWidth: 300,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row, rowNode }) =>
        rowNode.depth !== 0 && (
          <Checkbox
            size="small"
            checked={row.categories.some((categoryId) => categoryId === category.externalId)}
            onChange={(e) => onCategoryChange(row, category, e.target.checked)}
            disabled={readOnly || rowEditingDisabled(row)}
          />
        ),
      renderHeader: () => <MultilineGridHeader label={category.name} />,
    });
  });

  const mainColumns: GridColDef<CommunicationMatrixModel>[] = [
    {
      sortable: false,
      resizable: false,
      field: "roles",
      width: 120,
      renderHeader: () => <MultilineGridHeader label="Portal Role" />,
      renderCell: ({ row, rowNode }) =>
        GroupedRowCell(
          rowNode,
          readOnly || rowEditingDisabled(row) ? (
            <PortalRoleChip role={resolvePortalRoles(row.roles)} />
          ) : (
            <PortalRoleSelector
              role={resolvePortalRoles(row.roles)}
              onChange={(role) => onChange({ ...row, roles: portalRowToArray(role) })}
              roleOptions={["None", ...orderedPortalRoles]}
            />
          )
        ),
    },
    {
      sortable: false,
      resizable: false,
      field: "emailNotification",
      width: 90,
      renderCell: ({ row, rowNode }) =>
        GroupedRowCell(
          rowNode,
          <Checkbox
            size="small"
            checked={row.emailNotification}
            disabled={readOnly || rowEditingDisabled(row)}
            onChange={(e) => onChange({ ...row, emailNotification: e.target.checked })}
          />
        ),
      renderHeader: () => <MultilineGridHeader label="Email Notification" />,
    },
    {
      sortable: false,
      resizable: false,
      field: "isPrimary",
      width: 80,
      renderCell: ({ row, rowNode }) =>
        GroupedRowCell(
          rowNode,
          <Checkbox
            size="small"
            checked={row.isPrimary}
            disabled={readOnly || rowEditingDisabled(row)}
            onChange={(_e, checked) => onChange({ ...row, isPrimary: checked })}
          />
        ),
      renderHeader: () => <MultilineGridHeader label="Primary Contact" />,
    },
  ];

  const columns = mainColumns.filter((col) => !(excludedColumns ?? []).includes(col.field)).concat(categoriesColumns);

  columns.push({
    field: "actions",
    headerName: "Actions",
    sortable: false,
    cellClassName: "grid-row-actions",
    width: 72,
    align: "right",
    headerAlign: "right",
    renderCell: ({ rowNode }) => {
      const getModelIdsForActions = (): string[] => {
        if (rowNode.type === "group") {
          return groupRowActionsDisabled ? [] : (rowNode.children as string[]);
        }
        if (rowNode.type === "leaf" && rowNode.depth === 0) {
          return groupRowActionsDisabled ? [] : [rowNode.id as string];
        }
        return childRowActionsDisabled ? [] : [rowNode.id as string];
      };

      const ids = getModelIdsForActions();
      if (ids.length === 0) {
        return null;
      }

      return (
        <ActionsMenuButton
          items={[
            {
              icon: <CloseRoundedIcon color="error" />,
              label: "Remove",
              disabled: readOnly,
              onClick: () => onRemove(ids),
            },
          ]}
        />
      );
    },
  });

  return [createCheckboxColumn({ groupsSelectionOnly: childRowActionsDisabled })].concat(columns);
};
